import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { IncidentFiled, useRealtime } from "../contexts/RealtimeContext";
import Button from "./Button";

/**
 * App-level incident alert overlay. Pops a screen-blocking modal when an
 * `incident_filed` socket event lands; plays a short attention beep;
 * doesn't dismiss until the dispatcher hits "Acknowledge". On ack we drop
 * the alert from the local queue (the live leg sub-tab row patched in by
 * the same event survives — see RealtimeContext). "View Mission" navigates
 * to the mission so the dispatcher can act.
 *
 * Mirrors <RealtimePanicOverlay /> but styled as an amber INCIDENT alert
 * (non-emergency) rather than the critical red panic tone, so the two are
 * visually distinct when both surface.
 *
 * Multiple incidents queue up — most recent first. Acknowledging one
 * surfaces the next.
 */
const RealtimeIncidentOverlay: React.FC = () => {
    const { incidentQueue, acknowledgeIncident } = useRealtime();
    const navigate = useNavigate();
    const lastSoundedIdRef = useRef<number | null>(null);

    const head: IncidentFiled | undefined = incidentQueue[0];

    // Play the alert tone exactly once per new incident. Browsers block
    // audio without a user gesture; if play() rejects we silently move
    // on — the visible modal is the primary affordance anyway.
    useEffect(() => {
        if (!head) return;
        const id = head.legIncidentId ?? -1;
        if (lastSoundedIdRef.current === id) return;
        lastSoundedIdRef.current = id;
        try {
            // Web Audio short two-tone beep generated inline — no asset to ship.
            const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
            const playTone = (freq: number, startSec: number, durSec: number) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = "square";
                osc.frequency.value = freq;
                gain.gain.setValueAtTime(0.001, ctx.currentTime + startSec);
                gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + startSec + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + startSec + durSec);
                osc.start(ctx.currentTime + startSec);
                osc.stop(ctx.currentTime + startSec + durSec);
            };
            playTone(740, 0, 0.18);
            playTone(560, 0.22, 0.18);
        } catch { /* gesture-blocked or unsupported — ignore */ }
    }, [head]);

    if (!head) return null;

    const incidentLabel = head.incidentName || head.incidentType || "Incident reported";
    // Prefer the human-readable name/ref from the payload; fall back to the
    // raw id so the dispatcher always has *something* to act on.
    const operatorLabel = head.operatorName
        || (head.operatorUserId != null ? `Operator #${head.operatorUserId}` : "Operator");
    const missionLabel = head.missionRef
        || (head.missionId != null ? `Mission #${head.missionId}` : null);
    const filedAt = head.filedAt ? new Date(head.filedAt) : new Date();
    const hasCoords = head.latitude != null && head.longitude != null;
    const locText = hasCoords
        ? `${(head.latitude as number).toFixed(5)}, ${(head.longitude as number).toFixed(5)}`
        : (head.address || "Location unknown");
    const mapsUrl = hasCoords
        ? `https://www.google.com/maps?q=${head.latitude},${head.longitude}`
        : null;

    const handleAcknowledge = () => {
        acknowledgeIncident(head.legIncidentId);
    };

    const handleOpenMission = () => {
        acknowledgeIncident(head.legIncidentId);
        if (head.missionId) {
            navigate(`/mission/${head.missionId}`);
        }
    };

    return (
        <Modal
            show
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
            contentClassName="border-0"
        >
            <div
                style={{
                    backgroundColor: "#7a4a10",
                    color: "#ffffff",
                    border: "3px solid #ff9f1c",
                    borderRadius: 6,
                    padding: 24,
                    boxShadow: "0 0 32px rgba(255, 159, 28, 0.6)",
                    fontFamily: "inherit",
                }}
            >
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <div
                        style={{
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            backgroundColor: "#ff9f1c",
                            boxShadow: "0 0 12px #ff9f1c",
                        }}
                    />
                    <div style={{ fontSize: "var(--fs-2xl)", fontWeight: 800, letterSpacing: 2 }}>
                        INCIDENT FILED
                    </div>
                    <div style={{ flex: 1 }} />
                    {incidentQueue.length > 1 ? (
                        <div
                            style={{
                                padding: "4px 10px",
                                border: "1px solid #ffffff",
                                borderRadius: 2,
                                fontSize: "var(--fs-2xs)",
                                fontWeight: 800,
                                letterSpacing: 1.4,
                            }}
                        >
                            {incidentQueue.length} ACTIVE
                        </div>
                    ) : null}
                </div>

                <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: "var(--fs-xl)", fontWeight: 700 }}>{incidentLabel}</div>
                    {head.incidentType && head.incidentName ? (
                        <div style={{ fontSize: "var(--fs-xs)", opacity: 0.8, marginTop: 2 }}>{head.incidentType}</div>
                    ) : null}
                    {missionLabel ? (
                        <div style={{ fontFamily: "Courier New, monospace", fontSize: "var(--fs-sm)", opacity: 0.85, letterSpacing: 1, marginTop: 4 }}>
                            {missionLabel}
                        </div>
                    ) : null}
                    <div style={{ fontSize: "var(--fs-xs)", opacity: 0.7, marginTop: 4 }}>
                        Reported by {operatorLabel} · {filedAt.toLocaleTimeString()}
                    </div>
                </div>

                <div
                    style={{
                        backgroundColor: "rgba(0,0,0,0.3)",
                        border: "1px solid rgba(255,255,255,0.2)",
                        borderRadius: 4,
                        padding: 12,
                        marginBottom: 16,
                    }}
                >
                    <div style={{ fontSize: "var(--fs-2xs)", fontWeight: 800, letterSpacing: 1.4, opacity: 0.8 }}>
                        LOCATION
                    </div>
                    <div style={{ fontFamily: "Courier New, monospace", fontSize: "var(--fs-md)", marginTop: 4 }}>
                        {locText}
                    </div>
                    {mapsUrl ? (
                        <a
                            href={mapsUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize: "var(--fs-2xs)", color: "#ffd54a", textDecoration: "underline", display: "inline-block", marginTop: 4 }}
                        >
                            Open in Google Maps →
                        </a>
                    ) : null}
                </div>

                {head.description ? (
                    <div
                        style={{
                            backgroundColor: "rgba(0,0,0,0.3)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            borderRadius: 4,
                            padding: 12,
                            marginBottom: 20,
                            fontSize: "var(--fs-sm)",
                            lineHeight: 1.5,
                        }}
                    >
                        <div style={{ fontSize: "var(--fs-2xs)", fontWeight: 800, letterSpacing: 1.4, opacity: 0.8, marginBottom: 4 }}>
                            DESCRIPTION
                        </div>
                        {head.description}
                    </div>
                ) : null}

                <div style={{ display: "flex", gap: 12 }}>
                    <Button
                        onClick={handleAcknowledge}
                        style={{ flex: 1, fontWeight: 700, letterSpacing: 1 }}
                    >
                        ACKNOWLEDGE
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleOpenMission}
                        disabled={!head.missionId}
                        style={{ flex: 1, fontWeight: 700, letterSpacing: 1 }}
                    >
                        VIEW MISSION →
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default RealtimeIncidentOverlay;
